<template>
  <div class="cashout-page">
    <div class="page-header d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <v-icon color="accent" size="28" class="mr-3">
          mdi-bank-transfer-out
        </v-icon>
        <div>
          <div class="page-title">Cashout Management</div>
          <div class="page-subtitle">Payment Processing Dashboard</div>
        </div>
      </div>

      <div class="d-flex align-center">
        <v-chip
          small
          color="success lighten-5"
          text-color="success darken-2"
          class="mr-3"
        >
          <v-icon left x-small>mdi-circle</v-icon>
          Live
        </v-chip>

        <v-btn icon @click="cashoutData" :loading="loading">
          <v-icon color="grey darken-1">mdi-refresh</v-icon>
        </v-btn>
      </div>
    </div>

    <v-container fluid class="pa-6">
      <!-- Summary Cards Row -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <div class="stat-card stat-card--paypal">
            <div class="stat-icon">
              <v-icon size="22" color="white">mdi-alpha-p-circle</v-icon>
            </div>
            <div class="stat-label">PayPal Total</div>
            <div class="stat-value">
              USD {{ formatAmount(totalPaypalAmount) }}
            </div>
            <div class="stat-sub">In-progress payouts</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="stat-card stat-card--ewallet">
            <div class="stat-icon">
              <v-icon size="22" color="white">mdi-wallet</v-icon>
            </div>
            <div class="stat-label">E-Wallet Total</div>
            <div class="stat-value">
              RM {{ formatAmount(totalEWalletAmount) }}
            </div>
            <div class="stat-sub">In-progress payouts</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="stat-card stat-card--records">
            <div class="stat-icon">
              <v-icon size="22" color="white">mdi-format-list-bulleted</v-icon>
            </div>
            <div class="stat-label">Records Shown</div>
            <div class="stat-value">{{ filteredCashout.length }}</div>
            <div class="stat-sub">Matching current filters</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="stat-card stat-card--status">
            <div class="stat-icon">
              <v-icon size="22" color="white">mdi-tag-outline</v-icon>
            </div>
            <div class="stat-label">Filter Status</div>
            <div class="stat-value text-truncate">
              {{ selectedStatus || "All" }}
            </div>
            <div class="stat-sub">Active status filter</div>
          </div>
        </v-col>
      </v-row>

      <!-- Main Content -->
      <v-row>
        <!-- Left: Filter Panel -->
        <v-col cols="12" md="3">
          <div class="panel">
            <div class="panel-header">
              <v-icon small color="accent" class="mr-2"
                >mdi-filter-variant</v-icon
              >
              <span class="panel-title">Filters</span>
            </div>
            <div class="panel-body">
              <div class="field-label">Status</div>
              <v-select
                v-model="selectedStatus"
                :items="filteredStatusOptions"
                outlined
                dense
                hide-details
                class="mb-4 custom-field"
                clearable
                placeholder="All statuses"
              />

              <div class="field-label">Cashout Type</div>
              <v-select
                v-model="selectedCashout"
                :items="cashoutOptions"
                outlined
                dense
                hide-details
                class="mb-4 custom-field"
                clearable
                placeholder="All types"
              />

              <div class="field-label">Email</div>
              <v-text-field
                v-model="email"
                outlined
                dense
                hide-details
                class="mb-5 custom-field"
                placeholder="Search by email"
                prepend-inner-icon="mdi-email-outline"
                clearable
              />

              <div class="field-label">From Date</div>
              <v-text-field
                v-model="createdAtFrom"
                type="date"
                outlined
                dense
                hide-details
                class="mb-4 custom-field"
              />

              <div class="field-label">To Date</div>
              <v-text-field
                v-model="createdAtTo"
                type="date"
                outlined
                dense
                hide-details
                class="mb-4 custom-field"
              />

              <v-btn
                @click="filterData"
                color="accent"
                depressed
                block
                class="filter-btn"
              >
                <v-icon left small>mdi-magnify</v-icon>
                Apply Filters
              </v-btn>

              <v-btn
                @click="resetFilters"
                text
                block
                class="mt-2 reset-btn"
                small
              >
                Reset
              </v-btn>
            </div>
          </div>

          <!-- Export Panel -->
          <div class="panel mt-4">
            <div class="panel-header">
              <v-icon small color="accent" class="mr-2">mdi-export</v-icon>
              <span class="panel-title">Export & Update</span>
            </div>
            <div class="panel-body">
              <v-alert
                color="warning"
                text
                dense
                icon="mdi-alert-circle-outline"
                class="mb-4 small-alert"
              >
                <span class="caption"
                  >Exporting immediately sets status to
                  <strong>Credited</strong>. Ensure payment is ready.</span
                >
              </v-alert>

              <v-btn
                @click="openExportDialog('PayPal')"
                outlined
                color="blue darken-2"
                block
                class="mb-2 export-btn"
                small
              >
                <v-icon left small>mdi-alpha-p-circle-outline</v-icon>
                Export PayPal
              </v-btn>

              <!-- <v-btn
                @click="openExportDialog('E Wallet')"
                outlined
                color="teal darken-1"
                block
                class="mb-2 export-btn"
                small
              >
                <v-icon left small>mdi-wallet-outline</v-icon>
                Export E-Wallet
              </v-btn> -->

              <v-divider class="my-3" />

              <v-btn
                @click="goToEWalletPage()"
                outlined
                color="teal darken-1"
                block
                class="mb-2 export-btn"
                small
              >
                <v-icon left small>mdi-wallet-outline</v-icon>
                Pay E-Wallet Per User
              </v-btn>

              <!-- <v-btn
                @click="triggerFileInput"
                outlined
                color="grey darken-2"
                block
                class="export-btn"
                small
              >
                <v-icon left small>mdi-upload</v-icon>
                Update E-Wallet CSV
              </v-btn>

              <input
                ref="inputFile"
                type="file"
                accept=".csv"
                @change="selectFile"
                style="display: none"
              />
              <div v-if="selectedFileName" class="file-chip mt-2">
                <v-icon x-small class="mr-1" color="teal"
                  >mdi-file-delimited</v-icon
                >
                <span class="caption">{{ selectedFileName }}</span>
              </div> -->
            </div>
          </div>
        </v-col>

        <!-- Right: Data Table -->
        <v-col cols="12" md="9">
          <div class="panel">
            <div class="panel-header d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon small color="accent" class="mr-2">mdi-table</v-icon>
                <span class="panel-title">Cashout Records</span>
              </div>
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                placeholder="Search records…"
                outlined
                dense
                hide-details
                class="custom-field search-field"
                clearable
              />
            </div>
            <v-data-table
              :headers="headers"
              :items="filteredCashout"
              :search="search"
              :loading="loading"
              loading-text="Fetching cashout records…"
              no-data-text="No records found for the selected filters"
              class="cashout-table"
              :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
            >
              <!-- Status chip slot -->
              <template v-slot:item.status="{ item }">
                <v-chip
                  x-small
                  :color="statusColor(item.status)"
                  dark
                  class="status-chip"
                >
                  {{ item.status }}
                </v-chip>
              </template>

              <!-- Cashout type slot -->
              <template v-slot:item.cashoutType="{ item }">
                <div class="d-flex align-center">
                  <v-icon
                    x-small
                    class="mr-1"
                    :color="
                      item.cashoutType === 'PAYPAL'
                        ? 'blue darken-2'
                        : 'teal darken-1'
                    "
                  >
                    {{
                      item.cashoutType === "PAYPAL"
                        ? "mdi-alpha-p-circle"
                        : "mdi-wallet"
                    }}
                  </v-icon>
                  <span class="type-label">{{ item.cashoutType }}</span>
                </div>
              </template>

              <!-- Date slot -->
              <template v-slot:item.createdAt="{ item }">
                <span class="date-text">{{ formatDate(item.createdAt) }}</span>
              </template>

              <!-- Points slot -->
              <template v-slot:item.pointsRedeemed="{ item }">
                <span class="points-text">{{ item.pointsRedeemed }}</span>
              </template>
            </v-data-table>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Export Confirmation Dialog -->
    <v-dialog
      v-model="dialog"
      max-width="420"
      overlay-color="black"
      overlay-opacity="0.5"
    >
      <v-card class="dialog-card">
        <div class="dialog-header">
          <v-icon
            size="32"
            :color="exportType === 'PayPal' ? 'blue darken-2' : 'teal darken-1'"
            class="mb-2"
          >
            {{ exportType === "PayPal" ? "mdi-alpha-p-circle" : "mdi-wallet" }}
          </v-icon>
          <div class="dialog-title">Confirm Export</div>
          <div class="dialog-sub">
            You are about to export <strong>{{ exportType }}</strong> cashout
            data
          </div>
        </div>
        <v-divider />
        <v-card-text class="pt-4">
          <v-alert dense text color="warning" icon="mdi-alert" class="mb-0">
            <span class="caption"
              >All matching records will be marked as
              <strong>Credited</strong> immediately. This cannot be
              undone.</span
            >
          </v-alert>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-btn text color="grey darken-1" @click="dialog = false"
            >Cancel</v-btn
          >
          <v-spacer />
          <v-btn
            depressed
            :color="exportType === 'PayPal' ? 'blue darken-2' : 'teal darken-1'"
            dark
            @click="confirmExport"
          >
            <v-icon left small>mdi-download</v-icon>
            Confirm & Export
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
var csvjson = require("csvjson");
import RestResource from "../../../services/dataServiceMobileV2";
const service = new RestResource();

export default {
  name: "CashoutManagement",
  data() {
    return {
      cashout: [],
      selectedFileName: null,
      csvData: null,
      filteredCashout: [],
      totalEWalletAmount: 0,
      totalPaypalAmount: 0,
      loading: false,
      headers: [
        { text: "Email", value: "email", width: "220px" },
        {
          text: "Points Redeemed",
          value: "pointsRedeemed",
          align: "right",
          width: "140px",
        },
        { text: "Status", value: "status", width: "120px" },
        { text: "Type", value: "cashoutType", width: "120px" },
        { text: "Date", value: "createdAt", width: "150px" },
      ],
      search: "",
      email: "",
      selectedStatus: "IN PROGRESS",
      selectedCashout: "",
      statusOptions: ["IN PROGRESS", "CREDITED", "REJECTED", "PROCESSING"],
      cashoutOptions: ["PAYPAL", "EWALLET"],
      dialog: false,
      exportType: "",
      createdAtFrom: "",
      createdAtTo: "",
    };
  },

  mounted() {
    this.cashoutData();
    this.getTotalCashout();
  },

  computed: {
    filteredStatusOptions() {
      return this.selectedCashout === "BANK TRANSFER"
        ? this.statusOptions
        : this.statusOptions.filter((s) => s !== "PROCESSING");
    },
  },

  methods: {
    cashoutData() {
      this.loading = true;
      const params = {};
      if (this.selectedStatus) params.status = this.selectedStatus;
      if (this.email) params.email = this.email;
      if (this.selectedCashout) params.cashoutType = this.selectedCashout;

      service
        .getCashoutData(params)
        .then((r) => {
          this.cashout = r.data;
          this.filteredCashout = r.data;
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },

    async getTotalCashout() {
      try {
        const data = await service.getTotalCashout();
        data.data.data.forEach((item) => {
          if (item.cashoutType === "PAYPAL")
            this.totalPaypalAmount = item.totalAmount;
          else if (item.cashoutType === "EWALLET")
            this.totalEWalletAmount = item.totalAmount;
        });
      } catch {
        console.error("Failed to fetch total cashout amounts");
      }
    },

    filterData() {
      this.cashoutData();
    },

    resetFilters() {
      this.selectedStatus = "IN PROGRESS";
      this.selectedCashout = "";
      this.email = "";
      this.cashoutData();
    },

    openExportDialog(type) {
      this.exportType = type;
      this.dialog = true;
    },

    confirmExport() {
      if (this.exportType === "PayPal") this.exportPayPal();
      else if (this.exportType === "E Wallet") this.exportEWallet();
      this.dialog = false;
    },

    exportPayPal() {
      this.loading = true;

      const payload = {
        type: "PayPal",
        ...(this.createdAtFrom && { createdAtFrom: this.createdAtFrom }),
        ...(this.createdAtTo && { createdAtTo: this.createdAtTo }),
      };

      service.exportPaypal(payload).then(() => {
        this.loading = false;
        this.cashoutData();
      });
    },

    exportEWallet() {
      this.loading = true;
      service.exportWallet({ type: "EWallet" }).then(() => {
        this.loading = false;
        this.cashoutData();
      });
    },

    triggerFileInput() {
      this.$refs.inputFile.click();
    },

    selectFile(event) {
      const file = event.target.files[0];
      this.selectedFileName = file ? file.name : null;
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.csvData = csvjson.toObject(e.target.result, {
            delimiter: ",",
            quote: '"',
          });
          this.updateBankTransferData(this.csvData);
        };
        reader.readAsText(file);
      }
    },

    async updateBankTransferData(data) {
      try {
        this.loading = true;
        await service.updateBankTransferStatus(data);
      } catch (error) {
        alert(error);
      } finally {
        this.loading = false;
      }
    },

    statusColor(status) {
      const map = {
        "IN PROGRESS": "orange darken-2",
        CREDITED: "green darken-1",
        REJECTED: "red darken-1",
        PROCESSING: "blue darken-1",
      };
      return map[status] || "grey";
    },

    formatAmount(val) {
      if (!val && val !== 0) return "—";
      return Number(val).toLocaleString("en-MY", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatDate(val) {
      if (!val) return "—";
      const d = new Date(val);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    goToEWalletPage() {
      this.$router.push({ name: "MobileV2CashoutEWallet" });
    },
  },
};
</script>

<style scoped>
/* ── Base & Theme ──────────────────────────────────────────── */
.cashout-app {
  background-color: #f0f2f5 !important;
  font-family: "DM Sans", "Segoe UI", sans-serif;
}

.main-bg {
  background-color: #f0f2f5 !important;
}

/* ── App Bar ───────────────────────────────────────────────── */
.app-bar-border {
  border-bottom: 1px solid #e2e6ea !important;
  background-color: #ffffff !important;
}

.page-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a202c;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 11px;
  color: #718096;
  letter-spacing: 0.3px;
  line-height: 1.2;
}

/* ── Stat Cards ────────────────────────────────────────────── */
.stat-card {
  border-radius: 12px;
  padding: 18px 20px;
  color: white;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.16);
}

.stat-card::after {
  content: "";
  position: absolute;
  right: -10px;
  top: -10px;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}

.stat-card--paypal {
  background: linear-gradient(135deg, #1565c0, #1976d2);
}
.stat-card--ewallet {
  background: linear-gradient(135deg, #00695c, #00897b);
}
.stat-card--records {
  background: linear-gradient(135deg, #4527a0, #5e35b1);
}
.stat-card--status {
  background: linear-gradient(135deg, #e65100, #f57c00);
}

.stat-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}

.stat-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  opacity: 0.85;
  margin-bottom: 2px;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 2px;
}

.stat-sub {
  font-size: 11px;
  opacity: 0.7;
}

/* ── Panels ────────────────────────────────────────────────── */
.panel {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e8ecf0;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.panel-header {
  display: flex;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid #f0f2f5;
  background: #fafbfc;
}

.panel-title {
  font-size: 13px;
  font-weight: 700;
  color: #2d3748;
  letter-spacing: 0.2px;
}

.panel-body {
  padding: 18px;
}

/* ── Fields ────────────────────────────────────────────────── */
.field-label {
  font-size: 11px;
  font-weight: 700;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 5px;
}

.custom-field ::v-deep .v-input__control .v-input__slot {
  border-radius: 8px !important;
  min-height: 36px !important;
}

.custom-field ::v-deep fieldset {
  border-color: #e2e8f0 !important;
  transition: border-color 0.15s;
}

.custom-field ::v-deep:hover fieldset {
  border-color: #a0aec0 !important;
}

.custom-field ::v-deep .v-input__slot {
  background: #f7fafc !important;
}

/* ── Buttons ───────────────────────────────────────────────── */
.filter-btn {
  border-radius: 8px !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
  height: 38px !important;
  background: #2b6cb0 !important;
  color: white !important;
}

.reset-btn {
  color: #718096 !important;
  font-size: 12px !important;
}

.export-btn {
  border-radius: 8px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  height: 34px !important;
}

/* ── Search field in table header ─────────────────────────── */
.search-field {
  max-width: 280px;
}

/* ── Data Table ────────────────────────────────────────────── */
.cashout-table {
  border-radius: 0 !important;
}

.cashout-table ::v-deep .v-data-table__wrapper {
  border-radius: 0 !important;
}

.cashout-table ::v-deep thead tr th {
  background-color: #f7fafc !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.6px !important;
  color: #718096 !important;
  border-bottom: 1px solid #e8ecf0 !important;
  padding: 0 16px !important;
  height: 42px !important;
}

.cashout-table ::v-deep tbody tr td {
  font-size: 13px !important;
  color: #2d3748 !important;
  border-bottom: 1px solid #f0f4f8 !important;
  padding: 0 16px !important;
  height: 48px !important;
}

.cashout-table ::v-deep tbody tr:hover td {
  background-color: #f7fafc !important;
}

.cashout-table ::v-deep tbody tr:last-child td {
  border-bottom: none !important;
}

/* ── Table cell styles ─────────────────────────────────────── */
.status-chip {
  font-size: 10px !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px !important;
  height: 20px !important;
  border-radius: 4px !important;
}

.type-label {
  font-size: 12px;
  font-weight: 600;
  color: #4a5568;
}

.date-text {
  color: #718096;
  font-size: 12px;
}

.points-text {
  font-weight: 600;
  color: #2d3748;
}

/* ── File chip ─────────────────────────────────────────────── */
.file-chip {
  display: flex;
  align-items: center;
  background: #f0fff4;
  border: 1px solid #c6f6d5;
  border-radius: 6px;
  padding: 5px 10px;
  overflow: hidden;
}

.file-chip span {
  color: #276749;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Alert ─────────────────────────────────────────────────── */
.small-alert ::v-deep .v-alert__content {
  font-size: 12px;
}

/* ── Dialog ────────────────────────────────────────────────── */
.dialog-card {
  border-radius: 14px !important;
  overflow: hidden;
}

.dialog-header {
  padding: 28px 28px 20px;
  text-align: center;
  background: #fafbfc;
}

.dialog-title {
  font-size: 18px;
  font-weight: 800;
  color: #1a202c;
  margin-bottom: 4px;
}

.dialog-sub {
  font-size: 13px;
  color: #718096;
}

.cashout-page {
  width: 100%;
  min-height: 100%;
}

.page-header {
  padding: 16px 24px;
  background: #fff;
  border-bottom: 1px solid #e2e6ea;
}
</style>
